import moment from "moment";
// models
import { Employee, EmployeeActivity, EmployeeActivityType, EvaCompany } from "src/app/domains/internal";

import { EvaCompanyService } from "../features/eva-company/services/evaCompany.service";
import { getWeekStartDate } from "../shared/helpers/date.helpers";

export enum EmployeeWeekActivityStatus {
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  NO_DATA = "NO_DATA",
  DRAFT = "DRAFT",
  WAITING_VALIDATION = "WAITING_VALIDATION",
}

export enum EmployeeWeekActivityStatusLabel {
  ACCEPTED = "Acceptée",
  REJECTED = "Refusée",
  NO_DATA = "Pas de données",
  DRAFT = "Brouillon",
  WAITING_VALIDATION = "En attente de validation",
}

export class EmployeeWeekActivity {
  employee_week_activity_id: string;
  employee_id: string;
  week: number;
  year: number;
  status: EmployeeWeekActivityStatus;
  comment: string;
  admin_comment: string;
  admin_validator: string;
  activities: EmployeeActivity[];

  employee: Employee;

  dailyActivities: EmployeeActivity[][];
  dailyActivitiesWeekend: EmployeeActivity[][];
  weekStatuses: Map<string, EmployeeWeekActivityStatus> = new Map();

  constructor() {
    this.status = EmployeeWeekActivityStatus.DRAFT;
    this.activities = [];
    this.weekStatuses = new Map();
  }

  get editAllowed() {
    return [
      EmployeeWeekActivityStatus.REJECTED,
      EmployeeWeekActivityStatus.NO_DATA,
      EmployeeWeekActivityStatus.DRAFT,
    ].includes(this.status);
  }

  get startDate() {
    return getWeekStartDate(this.year, this.week);
  }

  public static createInstance({
    employee_week_activity_id,
    employee_id,
    week,
    year,
    status,
    comment,
    admin_comment,
    admin_validator,
    activities,
    employee,
  }): EmployeeWeekActivity {
    const e = new EmployeeWeekActivity();
    e.employee_week_activity_id = employee_week_activity_id;
    e.employee_id = employee_id;
    e.week = week;
    e.year = year;
    e.comment = comment;
    e.admin_validator = admin_validator;
    e.admin_comment = admin_comment;
    e.status = status;
    e.activities = [];
    for (const rawActivity of activities) {
      const a = EmployeeActivity.createInstance(rawActivity);
      e.activities.push(a);
    }
    if (employee) {
      e.employee = Employee.createInstance(employee);
    }
    e.dailyActivities = e.getDailyActivities();
    e.dailyActivitiesWeekend = e.getDailyActivitiesWeekend();
    return e;
  }

  public static createInstances(weekActivities: any[]): EmployeeWeekActivity[] {
    return weekActivities.map(EmployeeWeekActivity.createInstance);
  }

  public isEditAllowed(): boolean {
    return ["NO_DATA", "DRAFT", "REJECTED"].includes(this.status);
  }

  getStatusForDate(date: Date): EmployeeWeekActivityStatus {
    if (this.weekStatuses.size === 0) return this.status;
    const m = moment(date);
    const key = `${m.isoWeekYear()}-${m.isoWeek()}`;
    return this.weekStatuses.get(key) ?? EmployeeWeekActivityStatus.DRAFT;
  }

  calculatedTotalHoursWorked: number = 0;
  calculatedTotalDaysWorked: number = 0;
  calculatedCostHours: number = 0;
  calculatedMealPaybackRequestCount: number = 0;
  calculatedMealPaybackRequestAmount: number = 0;
  calculatedCost: number = 0;

  get costHours(): number {
    let total = 0.0;
    if (this.activities) {
      total += this.activities
        .filter((x) => x.activity_type === EmployeeActivityType.WORKING)
        .reduce((tot, item) => tot + item.activity_cost, 0);
    }
    return total;
  }

  get cost(): number {
    return this.costHours + this.mealPaybackRequestAmount;
  }

  get mealPaybackRequestCount(): number {
    const activities = this.activities.filter(
      (x) => x.activity_type === EmployeeActivityType.MEAL_BREAK && x.meal_payback_request,
    );
    return activities && this.isMealBreakAllowed ? activities.length : 0;
  }

  get isMealBreakAllowed(): boolean {
    let is_meal_break_allowed = false;
    try {
      is_meal_break_allowed = JSON.parse(localStorage.getItem("currentEvaCompany")).is_meal_break_allowed;
    } catch (error) {
      is_meal_break_allowed = false;
      console.error(error);
    }

    return is_meal_break_allowed;
  }

  get mealPaybackRequestAmount(): number {
    const totalAmount = this.activities.reduce((amount, activity) => {
      if (
        activity.activity_type === EmployeeActivityType.MEAL_BREAK &&
        activity.meal_payback_request &&
        this.isMealBreakAllowed
      ) {
        amount += activity.meal_payback_amount;
      }
      return amount;
    }, 0);

    return totalAmount;
  }

  get totalDaysWorked(): number {
    return this.totalHoursWorked / 8.25;
  }

  get totalHoursWorked(): number {
    let total = 0.0;
    if (this.activities) {
      total += this.activities
        .filter((x) => [EmployeeActivityType.WORKING].includes(x.activity_type))
        .reduce((tot, item) => tot + item.hours_count, 0);
    }
    return total;
  }

  get totalHoursOff(): number {
    return this.totalDaysOff * 8.25;
  }

  get totalDaysOff(): number {
    let total = 0.0;
    for (const activity of this.activities) {
      if (activity.activity_type === EmployeeActivityType.ABSENCE) {
        const currentDateCompare = new Date(activity.start_date);
        const endCompare = new Date(activity.end_date);

        while (currentDateCompare < endCompare) {
          total += 0.5;
          currentDateCompare.setTime(currentDateCompare.getTime() + 12 * 60 * 60 * 1000);
        }
      }
    }
    return total;
  }

  get weekStartDate(): Date {
    return moment().year(this.year).isoWeek(this.week).startOf("isoWeek").toDate();
  }

  get weekEndDate(): Date {
    return moment().year(this.year).isoWeek(this.week).endOf("isoWeek").toDate();
  }

  public getDailyActivitiesWeekend(): EmployeeActivity[][] {
    let dailyActivities: EmployeeActivity[][] = [];

    for (let i = 0; i < 7; i++) {
      dailyActivities[i] = [];
    }

    try {
      for (let activity of this.activities) {
        if (
          activity.activity_type === EmployeeActivityType.ABSENCE &&
          activity.absence_time_selection_type === "PREDEFINED"
        ) {
          // split multiple days activity into 1 activity per day
          const currentDay = moment(activity.start_date).startOf("day");
          const lastDay = moment(activity.end_date).startOf("day");
          while (currentDay.isSameOrBefore(lastDay)) {
            const dayIndex = currentDay.isoWeekday() - 1;
            if (dayIndex < 7) {
              const splitStart = moment.max(moment(activity.start_date), moment(currentDay)).toDate();
              const splitEnd = moment.min(moment(activity.end_date), moment(currentDay).endOf("day")).toDate();
              const splitActivity = Object.assign(new EmployeeActivity(), activity);
              splitActivity.start_date = splitStart;
              splitActivity.end_date = splitEnd;
              dailyActivities[dayIndex].push(splitActivity);
            }
            currentDay.add(1, "day");
          }
        } else if (
          [
            EmployeeActivityType.MEAL_BREAK,
            EmployeeActivityType.SCHEDULE,
            EmployeeActivityType.WORKING,
            EmployeeActivityType.OTHER_ACTIVITY,
          ].includes(activity.activity_type)
        ) {
          dailyActivities[moment(activity.start_date).isoWeekday() - 1].push(activity);
        } else if (
          activity.activity_type === EmployeeActivityType.ABSENCE &&
          activity.absence_time_selection_type === "CUSTOM"
        ) {
          dailyActivities[moment(activity.start_date).isoWeekday() - 1].push(activity);
        }
      }
    } catch (ex) {
      console.error(ex);
    }

    for (let i = 0; i < 7; i++) {
      dailyActivities[i] = dailyActivities[i].sort((a, b) => (a.start_date > b.start_date ? 1 : -1));
    }

    return dailyActivities;
  }

  public getDailyActivities(): EmployeeActivity[][] {
    let dailyActivities: EmployeeActivity[][] = [];

    for (let i = 0; i < 5; i++) {
      dailyActivities[i] = [];
    }

    try {
      for (let activity of this.activities) {
        if (
          activity.activity_type === EmployeeActivityType.ABSENCE &&
          activity.absence_time_selection_type === "PREDEFINED"
        ) {
          // split multiple days activity into 1 activity per day
          const currentDay = moment(activity.start_date).startOf("day");
          const lastDay = moment(activity.end_date).startOf("day");
          while (currentDay.isSameOrBefore(lastDay)) {
            const dayIndex = currentDay.isoWeekday() - 1;
            if (dayIndex < 5) {
              const splitStart = moment.max(moment(activity.start_date), moment(currentDay)).toDate();
              const splitEnd = moment.min(moment(activity.end_date), moment(currentDay).endOf("day")).toDate();
              const splitActivity = Object.assign(new EmployeeActivity(), activity);
              splitActivity.start_date = splitStart;
              splitActivity.end_date = splitEnd;
              dailyActivities[dayIndex].push(splitActivity);
            }
            currentDay.add(1, "day");
          }
        } else if (
          [
            EmployeeActivityType.MEAL_BREAK,
            EmployeeActivityType.SCHEDULE,
            EmployeeActivityType.WORKING,
            EmployeeActivityType.OTHER_ACTIVITY,
          ].includes(activity.activity_type)
        ) {
          let index = moment(activity.start_date).isoWeekday() - 1;
          if (index < dailyActivities.length) {
            dailyActivities[moment(activity.start_date).isoWeekday() - 1].push(activity);
          }
        } else if (
          activity.activity_type === EmployeeActivityType.ABSENCE &&
          activity.absence_time_selection_type === "CUSTOM"
        ) {
          dailyActivities[moment(activity.start_date).isoWeekday() - 1].push(activity);
        }
      }
    } catch (ex) {
      console.error(ex);
    }

    for (let i = 0; i < 5; i++) {
      dailyActivities[i] = dailyActivities[i].sort((a, b) => (a.start_date > b.start_date ? 1 : -1));
    }

    return dailyActivities;
  }
}
